import { useCallback, useEffect, useRef, useState } from "react";
import {
  createUsePuck,
  registerOverlayPortal,
  useGetPuck,
} from "@measured/puck";
import { resolveCmsIcon } from "@/lib/cms-puck/blocks/icons";
import {
  normalizeSocialLinkItems,
  type SocialLinkItem,
  type SocialLinksAlign,
} from "@/lib/cms-puck/blocks/social-links";
import {
  MAP_ASPECT_CLASS,
  normalizeMapEmbedUrl,
  type MapAspect,
} from "@/lib/cms-puck/blocks/map-embed";
import { type BlockStyle } from "@/lib/cms-puck/block-style";
import { useBlockTextCss } from "@/hooks/use-block-style-css";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import {
  resolveCompositeInnerLayout,
  type LayoutGap,
} from "@/lib/cms-puck/layout";

/**
 * ContactFormPreview — aperçu STATIQUE du bloc Formulaire de contact (le vrai
 * formulaire — envoi Mailjet, i18n du site — ne vit que sur le site publié,
 * cf. PuckContactForm côté front). Libellés français indicatifs, champs
 * désactivés ; avertissement si le destinataire n'est pas configuré.
 */
export function ContactFormPreview({ recipientEmail }: { recipientEmail?: string }) {
  const inputCls =
    "h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-400";
  return (
    <div className="mx-auto w-full max-w-xl">
      {!recipientEmail?.trim() ? (
        <p className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {cmsNew(
            "cms.new.preview.contactForm.recipientMissing",
            "Email destinataire non configuré — le formulaire ne s'affichera pas sur le site.",
          )}
        </p>
      ) : null}
      <div className="space-y-4">
        <div className="space-y-1.5">
          <p className="text-sm font-medium">
            {cmsNew("cms.new.preview.contactForm.subject", "Sujet")}
          </p>
          <div className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">
            {cmsNew("cms.new.preview.contactForm.email", "Votre email")}
          </p>
          <div className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium">
            {cmsNew("cms.new.preview.contactForm.message", "Message")}
          </p>
          <div className="h-24 w-full rounded-md border border-slate-200 bg-white" />
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span className="inline-block h-4 w-4 rounded border border-slate-300 bg-white" />
          Recevoir une copie de ce message
        </div>
        <span className="inline-flex w-full items-center justify-center rounded px-5 py-2 text-sm font-semibold bg-slate-900 text-white">
          Envoyer
        </span>
      </div>
    </div>
  );
}

/**
 * QuoteBlock — citation + auteur. Traitement du texte (onglet Design) appliqué
 * INLINE sur la citation ET l'auteur pour battre leurs classes Tailwind. Parité front.
 */
export function SocialLinksBlockPreview({
  links,
  align,
  style,
}: {
  links: SocialLinkItem[];
  align: SocialLinksAlign;
  style?: BlockStyle;
}) {
  const items = normalizeSocialLinkItems(links);
  const textCss = useBlockTextCss(style);
  const justify =
    align === "center"
      ? "justify-center"
      : align === "right"
        ? "justify-end"
        : "justify-start";
  if (items.length === 0) {
    return (
      <div className="text-sm text-slate-400">
        Ajoutez des liens sociaux
      </div>
    );
  }
  return (
    <nav className={`flex flex-wrap gap-3 ${justify}`}>
      {items.map((item, i) => {
        const Icon = resolveCmsIcon(item.platform);
        return (
          <a
            key={`${item.platform}-${i}`}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            style={textCss}
          >
            <Icon className="h-4 w-4" />
            {item.label || item.platform}
          </a>
        );
      })}
    </nav>
  );
}

export function MapEmbedBlockPreview({
  embedUrl,
  title,
  aspect,
  gap,
  gapCustomPx,
}: {
  embedUrl: string;
  title: string;
  aspect: MapAspect;
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
}) {
  const url = normalizeMapEmbedUrl(embedUrl);
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    withAlignItems: false,
  });
  return (
    <section
      className={`flex flex-col ${inner.className}`}
      style={inner.style}
    >
      {title ? (
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      ) : null}
      {url ? (
        <div
          className={`overflow-hidden rounded-lg ${MAP_ASPECT_CLASS[aspect]}`}
        >
          <iframe
            src={url}
            title={title || "Carte"}
            className="h-full w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      ) : (
        <div className="flex aspect-video items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-400">
          Collez l&apos;URL d&apos;embed (Google Maps, OpenStreetMap…)
        </div>
      )}
    </section>
  );
}

/** Aperçu éditeur d'un item d'accordéon (titre + slot réponse, toujours ouvert). */
export function AccItemPreview({
  title,
  body,
}: {
  title: React.ReactNode;
  body: React.ReactNode;
}) {
  return (
    <div className="border-b border-slate-200 py-3">
      <h3 className="text-base font-medium text-slate-900">{title}</h3>
      <div className="mt-2 text-slate-600">{body}</div>
    </div>
  );
}

type TabsBlockProps = {
  /** Id du bloc : le clic sur un onglet le (re)sélectionne dans l'éditeur. */
  id?: string;
  isEditing?: boolean;
  items: { title: string; body: React.ReactNode }[];
};

const usePuckSelector = createUsePuck();

/** Le nœud (ou un de ses descendants, slots compris) a-t-il cet id ? */
function containsBlock(value: unknown, id: string): boolean {
  if (!Array.isArray(value)) return false;
  return value.some((node: { props?: Record<string, unknown> } | null) => {
    if (!node?.props) return false;
    if (node.props.id === id) return true;
    return Object.values(node.props).some((v) => containsBlock(v, id));
  });
}

/**
 * TabsBlock — aperçu ÉDITEUR du bloc Onglets, même rendu que `PuckTabs` sur le
 * site (un onglet visible à la fois) : cliquer un onglet du canevas affiche son
 * contenu à éditer. Les corps inactifs restent montés (masqués) pour que Puck
 * garde tous les slots. Onglets à titre vide ignorés. KEEP IN SYNC avec PuckTabs.
 *
 * Au survol, le calque de Puck recouvre le bloc et reçoit le clic : la barre
 * d'onglets est donc déclarée à Puck (`registerOverlayPortal`, comme ses champs
 * de texte en ligne). Le clic s'arrête au bouton (sinon le canevas désélectionne)
 * et resélectionne le bloc lui-même (sinon un bloc parent prendrait la main).
 * `useGetPuck` lève une erreur hors de `<Puck>` (aperçu des modèles) : on ne
 * l'appelle que dans la branche édition, cf. `LayoutSlot`.
 */
export function TabsBlock(props: TabsBlockProps) {
  return props.isEditing && props.id ? (
    <TabsBlockEditing {...props} id={props.id} />
  ) : (
    <TabsBlockInner items={props.items} />
  );
}

function TabsBlockEditing({ id, items }: TabsBlockProps & { id: string }) {
  const getPuck = useGetPuck();
  // Onglet (0 à 3) qui contient le bloc sélectionné : sélection depuis
  // l'arborescence, annuler / rétablir… sinon il resterait dans un panneau masqué.
  const selectedSlot = usePuckSelector((s) => {
    const selectedId = s.selectedItem?.props.id;
    // `getItemById` lève si le bloc vient de quitter la page (suppression, annuler).
    if (!selectedId || selectedId === id || !s.getSelectorForId(id)) return null;
    const props = s.getItemById(id)?.props as Record<string, unknown> | undefined;
    if (!props) return null;
    const slot = [1, 2, 3, 4].findIndex((n) =>
      containsBlock(props[`tab${n}Body`], selectedId),
    );
    return slot >= 0 ? slot : null;
  });
  const selectBlock = useCallback(() => {
    const { selectedItem, getSelectorForId, dispatch } = getPuck();
    if (selectedItem?.props.id === id) return;
    const itemSelector = getSelectorForId(id);
    if (itemSelector) dispatch({ type: "setUi", ui: { itemSelector } });
  }, [getPuck, id]);
  return (
    <TabsBlockInner
      items={items}
      onTabClick={selectBlock}
      focusSlot={selectedSlot}
      isEditing
    />
  );
}

function TabsBlockInner({
  items,
  onTabClick,
  focusSlot = null,
  isEditing = false,
}: {
  items: TabsBlockProps["items"];
  onTabClick?: () => void;
  /** Onglet à afficher (celui du bloc sélectionné), sinon `null`. */
  focusSlot?: number | null;
  isEditing?: boolean;
}) {
  // Rang d'origine (0 à 3) gardé en clé : vider un titre ne fait pas glisser
  // le contenu d'un onglet dans le panneau d'un autre.
  const visible = items
    .map((it, slot) => ({ ...it, slot }))
    .filter((it) => it.title && it.title.trim().length > 0);
  // Onglet actif retenu par son rang d'origine, pas par sa position visible.
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  useEffect(() => {
    if (focusSlot != null) setActiveSlot(focusSlot);
  }, [focusSlot]);
  const tabListRef = useRef<HTMLDivElement>(null);
  const hasTabs = visible.length > 0;
  // Pas de `disableDragOnFocus` : des boutons n'ont pas de texte à sélectionner,
  // la barre reste une poignée pour déplacer le bloc.
  useEffect(
    () =>
      isEditing
        ? registerOverlayPortal(tabListRef.current, {
            disableDragOnFocus: false,
          })
        : undefined,
    [isEditing, hasTabs],
  );
  if (!hasTabs) return null;
  const current = Math.max(
    0,
    visible.findIndex((it) => it.slot === activeSlot),
  );
  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      <div
        ref={tabListRef}
        role="tablist"
        className="mb-3 flex w-full justify-start overflow-x-auto p-1 lg:overflow-x-hidden"
      >
        {visible.map((it, i) => {
          const isActive = current === i;
          return (
            <button
              key={it.slot}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={(e) => {
                e.stopPropagation();
                setActiveSlot(it.slot);
                onTabClick?.();
              }}
              className={`flex flex-1 items-center justify-center whitespace-nowrap border-b px-3 py-1 text-sm font-medium transition-colors ${
                isActive
                  ? "border-b-2 border-current text-current"
                  : "cms-page-text-muted border-transparent"
              }`}
            >
              {it.title}
            </button>
          );
        })}
      </div>
      {visible.map((it, i) => (
        <div
          key={it.slot}
          role="tabpanel"
          hidden={current !== i}
          className={`mt-2 w-full flex-col align-top text-base md:w-[75%] ${
            current === i ? "inline-flex" : "hidden"
          }`}
        >
          {it.body}
        </div>
      ))}
    </div>
  );
}
